import { useContext } from 'react'
import Link from 'next/link'
import PropTypes from 'prop-types'

// Context
import { ModalContext } from '../../context/ModalContext'
import { UserContext } from '../../context/UserContext'

// Internal components
import Svg from '../Svg/Svg'

const Button = (props) => {
  const { modifiers, label, target, icon, inlineIcon, image, href, link, modal, disabled, onClick, hook } = props

  // Contexts
  const modalContext = useContext(ModalContext)
  const userContext = useContext(UserContext)

  const iconBefore = icon && icon.before ? icon.before : false
  const modifierClasses = modifiers ? ' ' + modifiers.join(' ') : ''

  const createMarkup = (copy) => ({ __html: copy })

  function showRipple(e) {
    const ripple = document.createElement('span')
    const rippleOffset = e.currentTarget.getBoundingClientRect()
    const ripplePoint = {
      x: e.pageX - rippleOffset.left,
      y: e.pageY + document.querySelector('body').getBoundingClientRect().top - rippleOffset.top
    }

    ripple.className = 'Button-ripple'
    ripple.style.top = ripplePoint.y + 'px'
    ripple.style.left = ripplePoint.x + 'px'

    e.currentTarget.appendChild(ripple)

    setTimeout(() => {
      ripple.parentNode.removeChild(ripple)
    }, 800)
  }

  const clickEvent = (e) => {
    showRipple(e)
    if (onClick) onClick(e)
    if (hook) modalContext.updateModalHooks(hook)
    if (modal?.type === 'close') {
      if (modal?.hook) {
        closeModalAfterSetHook(modal)
      } else {
        if (!modalContext.modalPrompt) modalContext.setModalIsActive(false)
        if (modalContext.modalPrompt) modalContext.setModalHasPrompt(true)
      }
      e.preventDefault()
    }
    if (modal?.type === 'api' || modal?.type === 'gallery') {
      if (modal?.hook) {
        openModalAfterSetHook(modal)
      } else {
        modalContext.setStateClass('is-loading')
        modalContext.openModal(modal)
        e.preventDefault()
      }
    }
    if (modal?.type === 'return') returnModalAfterSetHook(modal)
  }

  const returnModalAfterSetHook = async (modal) => {
    modalContext.setStateClass('is-loading')
    const hookUpdated = await modalContext.updateModalHooks(modal?.hook)
    if (hookUpdated && modalContext.modalReturn) {
      userContext.updateSession()
      setTimeout(() => {
        if (modalContext.modalReturn.id) modalContext.setModalId(modalContext.modalReturn.id)
        if (modalContext.modalReturn.route) modalContext.setModalRoute(modalContext.modalReturn.route)
        if (modalContext.modalReturn.modifier) modalContext.setModalModifier(modalContext.modalReturn.modifier)
        modalContext.setModalMandatory(modalContext?.modalReturn?.mandatory)
        modalContext.setModalReturn(false)
      }, 5000)
    } else {
      modalContext.setModalIsActive(false)
    }
  }

  const openModalAfterSetHook = async (modal) => {
    modalContext.setStateClass('is-loading')
    const hookUpdated = await modalContext.updateModalHooks(modal?.hook)
    if (hookUpdated && modalContext.modalReturn) {
      modalContext.openModal(modal)
    } else {
      modalContext.setModalIsActive(false)
    }
  }

  const closeModalAfterSetHook = async (modal) => {
    modalContext.setStateClass('is-loading')
    const hookUpdated = await modalContext.updateModalHooks(modal?.hook)
    if (hookUpdated) {
      userContext.updateSession()
      setTimeout(() => {
        modalContext.setModalIsActive(false)
      }, 100)
    } else {
      modalContext.setModalIsActive(false)
    }
  }

  const ButtonContent = () => {
    return (
      <>
        {image && (
          <span className="Button-image">
            <Svg id={image.svgId} />
          </span>
        )}
        {label && !inlineIcon && (
          <span className="Button-label">
            <span dangerouslySetInnerHTML={createMarkup(label)}></span>
          </span>
        )}
        {label && inlineIcon && (
          <span className="Button-label">
            <span dangerouslySetInnerHTML={createMarkup(label)}></span>
            <Svg id={inlineIcon?.id} modifiers={['Button-inlineIcon']} />
          </span>
        )}
        {icon && (
          <span className="Button-icon">
            <Svg id={icon.id} />
          </span>
        )}
      </>
    )
  }

  return (
    <>
      {!link && !href && !modal && (
        <button
          type="button"
          className={`Button${modifierClasses}${iconBefore ? ' Button--iconBefore' : ''}`}
          onClick={clickEvent}
        >
          <ButtonContent />
        </button>
      )}

      {modal?.type === 'submit' && (
        <button
          type="submit"
          value="Submit"
          disabled={disabled}
          className={`Button${modifierClasses}${iconBefore ? ' Button--iconBefore' : ''}`}
          onClick={clickEvent}
        >
          <ButtonContent />
        </button>
      )}

      {modal?.type === 'gallery' && (
        <button
          modal-type={modal.type}
          data-youtubeid={modal?.youtubeId}
          data-youtubetitle={modal?.youtubeTitle}
          className={`Button${modifierClasses}${iconBefore ? ' Button--iconBefore' : ''}`}
          onClick={clickEvent}
        >
          <ButtonContent />
        </button>
      )}

      {modal?.type === 'api' && (
        <button
          modal-type={modal.type}
          modal-route={`${modal.route}${modal.id ? `/${modal.id}` : ''}`}
          className={`Button${modifierClasses}${iconBefore ? ' Button--iconBefore' : ''}`}
          onClick={clickEvent}
        >
          <ButtonContent />
        </button>
      )}

      {modal?.type === 'return' && (
        <button
          modal-type={modal.type}
          modal-route={`${modal.route}${modal.id ? `/${modal.id}` : ''}`}
          className={`Button${modifierClasses}${iconBefore ? ' Button--iconBefore' : ''}`}
          onClick={clickEvent}
        >
          <ButtonContent />
        </button>
      )}

      {modal?.type === 'close' && (
        <button className={`Button${modifierClasses}${iconBefore ? ' Button--iconBefore' : ''}`} onClick={clickEvent}>
          <ButtonContent />
        </button>
      )}

      {link && (
        <Link href={link.href}>
          <a className={`Button${modifierClasses}${iconBefore ? ' Button--iconBefore' : ''}`} onClick={clickEvent}>
            <ButtonContent />
          </a>
        </Link>
      )}

      {href && (
        <a
          className={`Button${modifierClasses}${iconBefore ? ' Button--iconBefore' : ''}`}
          onClick={clickEvent}
          href={href}
          target={target}
        >
          <ButtonContent />
        </a>
      )}
    </>
  )
}

Button.propTypes = {
  label: PropTypes.string,
  modifiers: PropTypes.array,
  target: PropTypes.string,
  href: PropTypes.string,
  link: PropTypes.object,
  modal: PropTypes.object,
  icon: PropTypes.object,
  inlineIcon: PropTypes.object,
  onClick: PropTypes.func,
  image: PropTypes.object,
  disabled: PropTypes.bool,
  hook: PropTypes.any
}

export default Button
